import { endUsedSave, hasUsedSave, startUsedSave } from './pendingUsedSaves';
import { closeTimerPill, msUntilStreakLanding } from '@utils/calendar/markUsedCelebration';
import { all, call, delay, fork, put, select, spawn, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { answerWaitingPopup } from '@utils/askInPopup';
import { askEstimateQuestion } from '@state/task/askQuestion';

import * as actions from './actions';
import { closeCalendarDialog } from './actions';
import * as semesterSelectors from '../semester/selectors';
import * as timerSelectors from '../timer/selectors';
import { apiCall } from '../common/operations';
import { getErrorMessage } from '../../api/network';
import { activityApi, calendarApi, courseApi, plannerApi } from '../../api';
import { PlanThisWeekRequest, shovelAiApi } from '../../api/shovelAiApi';
import storage from '../../utils/storage';
import { buildPlanInputs, formatPlanResult, plannedTaskIds, withCreatedReviewIds } from '@utils/taskPlanning';
import { AUTO_PLAN_INTRO_TOTAL_MS, autoPlanDays, autoPlanDelays } from '@utils/calendar/autoPlanIntro';
import { markFreedTime, POOF_SETTLE_MS, poofPlannedSessions } from '@utils/calendar/poof';
import { alert, NotificationMode } from '@components/common/snackbarNotifications/Notifications';
import t from '../../i18n/t';
import { GetTasksForPlanningRequest } from './types';
import { taskApi } from 'shovel-lib';
import { PILE_TODO_TAKE_PARAM } from '../../utils/constants/pile';
import {
  getAwakeTimeEvents,
  getBusyEventsForWeekPlanning,
  getEventEndsForWeekPlanning,
  getFreeTimeEventsWeek,
  getPlannedEvent
} from './selectors';
import { getSelectedCourseId } from '../workload/selectors';
import { workloadActions } from '../workload';
import { selectCommitment } from '../workload/actions';
import { SidebarItemType } from '../workload/types';
import { timerActions } from '../timer';
import { taskActions } from '../task';
import monthApi from '../../api/monthApi';
import moment from 'moment';
import { DATE_FORMAT, toMomentDate } from 'shovel-lib/utils/timeUtils';
import { eventActions } from '@state/events';
import weekApi from '@api/weekApi';
import { MarkUsedResponse, plannerApi as webPlannerApi, UnplanResponse } from '@api/plannerApi';
import { actionIdOf } from '@api/activityLogApi';
import { darkTheme, lightTheme } from '../../assets/themes';
import { RootState } from '@state/types';
import { CalendarEvent, DeletePlannedTaskResponse, PlanTasksRequest, TimerStatus } from 'shovel-lib/types';
import { getElapsedSeconds } from '@utils/timer';
import { undoActions, undoSelectors } from '@state/undo';
import { SessionPlace, UndoEntry } from '@state/undo/types';
import { paceDiffersEnough } from '@utils/constants/task';
import { clearHighlightLater, planSessions } from './planSessions';
import { planAfterOvertime } from '@utils/bump';
import { withPlanningDefaults } from '@utils/taskPlanning';
import { getBumpInputs } from './selectors';
import { applyTogether } from '../common/actions';
import { followBlocks } from '@utils/calendar/floatToNow';
import { bumpNewSession, bumpSessions, putBackBumped } from './bumpers';
import { endBumpersPreview } from '@utils/calendar/bumpersPreview';

// A moment after a dot lands on the streak's flame before the pill beside it starts closing.
const STREAK_SETTLE_MS = 250;

const sessionIds = (events: { id?: string | number }[]) => events.map(event => Number(event.id));

// A study session as the server sends it, with what it says about who placed it and the minutes logged on it.
type PlannedSession = CalendarEvent & { editedByStudent?: boolean; used?: number };

function* getCalendarData() {
  const semesterId = yield select(semesterSelectors.getId);

  const { ok, ...response } = yield apiCall(calendarApi.calendarEvents, semesterId);

  if (ok) {
    yield put(actions.calendarData.success(response.data));
  } else {
    yield put(actions.calendarData.failure(response));
  }
}

function* getDatesContainingTasks(action: ReturnType<typeof actions.getDatesContainingTasks.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { startDate: start, endDate: end } = action.payload;

  const startDate = moment(start);
  const endDate = moment(end);

  const { ok, ...response } = yield apiCall(
    monthApi.getDatesContainingTasks,
    semesterId,
    startDate.format(DATE_FORMAT),
    endDate.format(DATE_FORMAT)
  );

  if (ok) {
    yield put(actions.getDatesContainingTasks.success({ ...response.data, startDate, endDate }));
  } else {
    yield put(actions.getDatesContainingTasks.failure(response));
  }
}

function* getPlannedTasks() {
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(calendarApi.calendarEvents, semesterId);
  if (ok) {
    yield put(actions.getPlannedTasks.success(response.data));
  } else {
    yield put(actions.getPlannedTasks.failure(getErrorMessage(response)));
  }
}

function* getCommitmentsData() {
  const semesterId = yield select(semesterSelectors.getId);

  const [coursesResponse, activitiesResponse] = yield all([
    apiCall(courseApi.getAll, semesterId),
    apiCall(activityApi.getAll, semesterId)
  ]);

  if (!coursesResponse.ok) {
    yield put(actions.commitmentsData.failure(getErrorMessage(coursesResponse)));
    return;
  }

  if (!activitiesResponse.ok) {
    yield put(actions.commitmentsData.failure(getErrorMessage(activitiesResponse)));
    return;
  }

  yield put(
    actions.commitmentsData.success({
      courses: coursesResponse.data,
      activities: activitiesResponse.data,
      usedColors: []
    })
  );
}

// Popup edits of one session this close together are one step: its date, then its time.
const POPUP_STEP_MS = 30 * 1000;
let lastPopupMove: { entry: Extract<UndoEntry, { kind: 'sessionMoved' }>; at: number } | null = null;

const placeOf = (event: CalendarEvent): SessionPlace => ({
  id: Number(event.id),
  start: toMomentDate(event.start).toISOString(),
  end: toMomentDate(event.end).toISOString()
});

/**
 * A session moved or stretched, kept for Undo without a toast: a toast on every drag would get annoying (Petr,
 * 2026-10-24). The calendar's Undo button and Cmd+Z take it back.
 */
function* recordSessionMove(
  how: 'moved' | 'resized',
  before: PlannedSession | undefined,
  after: CalendarEvent,
  actionId: string | undefined,
  fromPopup?: boolean
) {
  if (!before) return;
  const to = placeOf(after);
  let from = placeOf(before);
  let { isUsed = false, editedByStudent = false } = before;
  const newest: UndoEntry | null = yield select(undoSelectors.getEntry);
  // The popup's earlier edit of the same session, while it is still the newest step: this one takes its place.
  const previous =
    fromPopup &&
    lastPopupMove &&
    lastPopupMove.entry === newest &&
    lastPopupMove.entry.to.id === to.id &&
    Date.now() - lastPopupMove.at < POPUP_STEP_MS
      ? lastPopupMove.entry
      : undefined;
  if (previous) {
    ({ from, isUsed, editedByStudent } = previous);
    actionId = previous.actionId;
  }
  lastPopupMove = null;
  if (from.start === to.start && from.end === to.end) {
    // Put back where it was in the popup: nothing left to undo.
    if (previous) yield put(undoActions.discard(previous));
    return;
  }
  // Stretched at either end, or moved: a popup's new date and new length together read as a move.
  const sameLength = toMomentDate(to.end).diff(to.start) === toMomentDate(from.end).diff(from.start);
  const stretched = !sameLength && (from.start === to.start || (how === 'resized' && from.end === to.end));
  const named = stretched ? 'resized' : 'moved';
  const title = before.title || '';
  const entry: Extract<UndoEntry, { kind: 'sessionMoved' }> = {
    kind: 'sessionMoved',
    how: named,
    title,
    from,
    to,
    isUsed,
    editedByStudent,
    fromPopup,
    actionId
  };
  const message = named === 'moved' ? t.SESSION_MOVED(title) : t.SESSION_RESIZED(title);
  yield put(undoActions.record({ entry, message, silent: true, replaces: previous }));
  if (fromPopup) lastPopupMove = { entry, at: Date.now() };
}

export function* movePlannedTask(action: ReturnType<typeof actions.movePlannedTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { id, callback, fromPopup, noUndo, ...request } = action.payload;
  const event = yield select(getPlannedEvent, id);
  const { ok, ...response } = yield apiCall(plannerApi.movePlannedTask, semesterId, id, request);
  if (ok) {
    yield put(actions.movePlannedTask.success({ oldEvent: event, newEvent: response.data }));
    if (callback) callback();
    if (!noUndo) yield call(recordSessionMove, 'moved', event, response.data, actionIdOf(response), fromPopup);
  } else {
    const errorMessage = getErrorMessage(response);
    yield put(actions.movePlannedTask.failure(errorMessage));
    alert(t[errorMessage] || errorMessage);
  }
}

export function* resizePlannedTask(action: ReturnType<typeof actions.resizePlannedTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { id, callback, revert, fromPopup, noUndo, logged, ...request } = action.payload;
  const event = yield select(getPlannedEvent, id);
  const { ok, ...response } = yield apiCall(plannerApi.resizePlannedTask, semesterId, id, request);
  if (ok) {
    yield put(actions.resizePlannedTask.success({ oldEvent: event, newEvent: response.data }));
    if (callback) callback();
    if (!noUndo) yield call(recordSessionMove, 'resized', event, response.data, actionIdOf(response), fromPopup);
    // More time typed on a used session (Petr, 2026-10-29: 1:50 typed, the next session left under it): the sessions
    // the longer block now runs into are bumped down behind it, as for a ✓ that logs more.
    const grew = !!event && new Date(response.data.end).getTime() > new Date(event.end).getTime();
    if (logged && grew) yield call(placeSessionsAfterOvertime, response.data);
  } else {
    const errorMessage = getErrorMessage(response);
    yield put(actions.resizePlannedTask.failure(errorMessage));
    // Revert the event instance in the calendar if the resize was triggered by dragging on the calendar
    if (revert) revert();
    alert(t[errorMessage] || errorMessage);
  }
}

function* deletePlannedTask(action: ReturnType<typeof actions.deletePlannedTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const activeTimer = yield select(timerSelectors.getActiveTimer);
  const event = yield select(getPlannedEvent, action.payload);
  const { ok, ...response } = yield apiCall(plannerApi.deletePlannedTask, semesterId, action.payload);
  if (ok) {
    // Goes in a puff of smoke rather than just blinking off; already gone if it was dragged onto the sidebar.
    const poofed: number = yield call(poofPlannedSessions, [action.payload]);
    if (poofed) yield delay(POOF_SETTLE_MS);
    // The free time left in its place fades in rather than popping in.
    if (event) yield call(markFreedTime, [timeHeld(event)]);
    yield put(actions.deletePlannedTask.success({ event, data: response.data }));
    if (activeTimer && activeTimer.plannedTaskId === Number(action.payload)) {
      yield put(timerActions.clearSession());
    }
    // None for a session marked used: putting it back as a plain session wouldn't restore the task's time.
    if (response.data?.removedSession) {
      yield put(
        undoActions.record({
          entry: {
            kind: 'planned',
            plannedSessionIds: [],
            unplannedSessions: [response.data.removedSession],
            reviewTaskIds: [],
            actionId: actionIdOf(response)
          },
          message: t.SESSIONS_UNPLANNED(1)
        })
      );
    }
  } else {
    const errorMessage = getErrorMessage(response);
    yield put(actions.deletePlannedTask.failure(errorMessage));
    alert(t[errorMessage] || errorMessage);
  }
}

function* getTasksOnDate(action: ReturnType<typeof actions.getTasksOnDate.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(calendarApi.tasksOnDate, semesterId, action.payload);
  if (ok) {
    yield put(actions.getTasksOnDate.success({ tasks: response.data, date: action.payload }));
  } else {
    yield put(actions.getTasksOnDate.failure(getErrorMessage(response)));
  }
}

function* createCourse(action: ReturnType<typeof actions.createCourse.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { isTaskList, ...payload } = action.payload;
  const { ok, ...response } = yield apiCall(courseApi.createCourse, semesterId, payload);
  if (ok) {
    yield put(actions.createCourse.success(response.data));
    if (isTaskList) {
      yield put(
        selectCommitment({
          id: response.data.id,
          type: SidebarItemType.COURSE,
          name: response.data.name,
          colorHex: response.data.colorHex
        })
      );
    }
  } else {
    yield put(actions.createCourse.failure(getErrorMessage(response)));
  }
}

function* updateCourse(action: ReturnType<typeof actions.updateCourse.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { id, ...request } = action.payload;
  const { ok, ...response } = yield apiCall(courseApi.updateCourse, semesterId, id, request);
  if (ok) {
    yield put(actions.updateCourse.success(response.data));
    yield put(eventActions.getEventsList.request({}));
  } else {
    yield put(actions.updateCourse.failure(getErrorMessage(response)));
  }
}

function* deleteCourse(action: ReturnType<typeof actions.deleteCourse.request>) {
  const id = action.payload;
  if (!id) {
    yield put(actions.deleteCourse.success({}));
    return;
  }
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(courseApi.deleteCourse, semesterId, id);
  if (ok) {
    const courseId = yield select(getSelectedCourseId);
    // refresh only if mode is ViewEverything
    yield put(actions.deleteCourse.success({ ...response.data, refreshTaskList: !courseId }));
    yield put(eventActions.getEventsList.request({}));
    yield put(actions.calendarData.request());
  } else {
    yield put(actions.deleteCourse.failure(getErrorMessage(response)));
  }
}

function* createActivity(action: ReturnType<typeof actions.createActivity.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { isTaskList, ...payload } = action.payload;
  const { ok, ...response } = yield apiCall(activityApi.createActivity, semesterId, payload);
  if (ok) {
    yield put(actions.createActivity.success(response.data));
    if (isTaskList) {
      yield put(
        selectCommitment({
          id: response.data.id,
          type: SidebarItemType.ACTIVITY,
          name: response.data.name,
          colorHex: response.data.colorHex
        })
      );
    }
  } else {
    yield put(actions.createActivity.failure(getErrorMessage(response)));
  }
}

function* updateActivity(action: ReturnType<typeof actions.updateActivity.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { id, ...request } = action.payload;
  const { ok, ...response } = yield apiCall(activityApi.updateActivity, semesterId, id, request);
  if (ok) {
    yield put(actions.updateActivity.success(response.data));
    yield put(eventActions.getEventsList.request({}));
  } else {
    yield put(actions.updateActivity.failure(getErrorMessage(response)));
  }
}

function* deleteActivity(action: ReturnType<typeof actions.deleteActivity.request>) {
  const id = action.payload;
  if (!id) {
    yield put(actions.deleteActivity.success({}));
    return;
  }
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(activityApi.deleteActivity, semesterId, id);
  if (ok) {
    yield put(actions.deleteActivity.success(response.data));
    yield put(eventActions.getEventsList.request({}));
  } else {
    yield put(actions.deleteActivity.failure(getErrorMessage(response)));
  }
}

// TODO : implement deleting of activity and course

function* planTask({ payload }: ReturnType<typeof actions.planTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { request, infoRef } = payload;
  const { ok, ...response } = yield apiCall(plannerApi.createPlannedTask, semesterId, request);
  if (ok) {
    // dispatching this action causes refetching of to_do and upcoming tasks
    // if we change implementation of fetching to clear the tasks list, this should go after analytics part
    yield put(closeCalendarDialog());
    if (infoRef) {
      infoRef.setEnd(response.data.end);
      yield delay(200);
      yield put(actions.planTask.success(response.data));
      infoRef.remove();
    } else {
      yield put(actions.planTask.success(response.data));
    }
    // With Bumpers, the sessions it landed on move down, and its Undo puts them back too.
    if (payload.bumps && (yield call(bumpNewSession, response.data, payload.bumped?.before))) return;
    // Drawn bumped at the drop, and nothing has to move after all: they go back.
    if (payload.bumped?.before.length) yield call(putBackBumped, payload.bumped.before);
    endBumpersPreview();
    yield put(
      undoActions.record({
        entry: {
          kind: 'planned',
          plannedSessionIds: sessionIds([response.data]),
          unplannedSessions: [],
          reviewTaskIds: [],
          actionId: actionIdOf(response)
        },
        message: t.SESSIONS_PLANNED(1)
      })
    );
  } else {
    if (payload.bumped?.before.length) yield call(putBackBumped, payload.bumped.before);
    endBumpersPreview();
    yield put(actions.planTask.failure(getErrorMessage(response)));
    if (infoRef?.remove) {
      infoRef.remove();
    }
  }
}

function* updateCalendarSlatHeight(action: ReturnType<typeof actions.updateCalendarSlatHeight>) {
  yield storage.setCalendarSlatHeightConfig(action.payload);
}

function* getTasksForPlanning({ payload }: { payload: GetTasksForPlanningRequest }) {
  const semesterId = yield select(semesterSelectors.getId);
  const { from, drop } = payload;

  const { ok, ...response } = yield apiCall(taskApi.todoTasksFromViewMore, semesterId, from, drop, PILE_TODO_TAKE_PARAM);
  if (ok) {
    yield put(actions.getTasksForPlanning.success(response.data));
  } else {
    yield put(actions.getTasksForPlanning.failure(getErrorMessage(response)));
  }
}

function* planTasks({ payload }: ReturnType<typeof actions.planTasks.request>) {
  const result: { planned: CalendarEvent[]; actionId?: string } | null = yield call(planSessions, payload);
  if (result) {
    const { planned, actionId } = result;
    yield put(
      undoActions.record({
        entry: {
          kind: 'planned',
          plannedSessionIds: sessionIds(planned),
          unplannedSessions: [],
          reviewTaskIds: [],
          actionId
        },
        message: t.SESSIONS_PLANNED(planned.length)
      })
    );
  }
}

function planningErrorMessage(data: unknown) {
  if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === 'string') return detail;
  }
  return getErrorMessage(data as any) || "Couldn't plan these dates. Try again.";
}

// Long enough for the Auto-plan dialog to be gone before its sessions come in, short enough not to read as a wait.
const AUTO_PLAN_DIALOG_CLOSE_MS = 150;

function* planThisWeek({ payload }: ReturnType<typeof actions.planThisWeek.request>) {
  // No "Planning…" toast: the dialog stays open on "Planning…" until this is done (`onDone`).
  const { replan, reviewDates, onDone, ...request } = payload;

  let planRequest: PlanThisWeekRequest = request;
  let unplannedCount = 0;
  // For Undo: what this planning made and took off, and its id in the activity log. The first step's id is passed on
  // to the next, so the reviews, a Replan's unplan and the run are one change.
  let reviewTaskIds: number[] = [];
  let unplannedSessions: unknown[] = [];
  let actionId: string | undefined;
  if (reviewDates) {
    // Make the reviews the preview showed, so they are planned as the tasks they now are. Before Replan reloads the
    // tasks, so it finds them there; without Replan the request still has them under their preview ids, swapped here.
    const reviewsResponse = yield apiCall(webPlannerApi.createReviews, request.semesterId, reviewDates.from, reviewDates.to);
    const created = reviewsResponse.ok ? reviewsResponse.data || [] : [];
    if (reviewsResponse.ok) actionId = actionIdOf(reviewsResponse);
    if (!reviewsResponse.ok) {
      alert("Couldn't add review sessions. Planning the rest.", NotificationMode.ERROR);
    }
    planRequest = { ...request, tasks: withCreatedReviewIds(request.tasks, created) };
    reviewTaskIds = created.map((review: { id: number }) => review.id);
    if (created.length > 0) {
      yield put(actions.reviewsCreated(created.length));
      yield put(workloadActions.getWorkload.request(undefined));
    }
  }
  if (replan) {
    const unplanResponse = yield apiCall(
      webPlannerApi.unplan,
      request.semesterId,
      { from: replan.from, to: replan.to, scope: replan.scope },
      actionId
    );
    if (!unplanResponse.ok) {
      const message = getErrorMessage(unplanResponse) || "Couldn't replan. Try again.";
      yield put(actions.planThisWeek.failure(message));
      if (onDone) yield call(onDone, false);
      alert(message, NotificationMode.ERROR);
      return;
    }
    unplannedCount = yield call(applyUnplanResult, unplanResponse.data);
    unplannedSessions = unplanResponse.data?.removedSessions || [];
    actionId = actionId ?? actionIdOf(unplanResponse);

    // The dialog built its request around the old blocks. Rebuild it from the freed calendar and fresh task times.
    const tasksResponse = yield apiCall(
      webPlannerApi.getActiveTasksInRange,
      request.semesterId,
      moment(replan.from).format(DATE_FORMAT),
      moment(replan.to).format(DATE_FORMAT)
    );
    if (!tasksResponse.ok) {
      const message = `Unplanned ${sessionsLabel(unplannedCount)}, but couldn't load tasks to plan. Run Auto-plan again.`;
      yield put(actions.planThisWeek.failure(message));
      if (onDone) yield call(onDone, false);
      alert(message, NotificationMode.ERROR);
      return;
    }
    // Theme only colors events; any theme gives the same free time.
    const freeTimes = yield select(getFreeTimeEventsWeek, { theme: lightTheme });
    const busyEvents = yield select(getBusyEventsForWeekPlanning, { theme: lightTheme });
    const plannedEvents = yield select((state: RootState) => state.calendar.plannedEvents);
    const awakeTimes = yield select(getAwakeTimeEvents, { theme: lightTheme });
    const eventEnds = yield select(getEventEndsForWeekPlanning, { theme: lightTheme });
    const inputs = buildPlanInputs({
      freeTimes,
      busyEvents,
      plannedEvents,
      awakeTimes,
      eventEnds,
      tasksInRange: tasksResponse.data || [],
      timeZone: request.timeZone,
      from: replan.from,
      to: replan.to,
      allBusyEvents: !!request.userPrompt
    });
    // noTimeTasks is for the dialog's own lists, not something the scheduler takes.
    const { noTimeTasks, ...requestInputs } = inputs;
    planRequest = { ...request, ...requestInputs };
    if (inputs.tasks.length === 0 || inputs.studyBlocks.length === 0) {
      yield put(actions.planThisWeek.success([]));
      if (onDone) yield call(onDone, true);
      yield put(
        undoActions.record({
          entry: { kind: 'planned', plannedSessionIds: [], unplannedSessions, reviewTaskIds, actionId },
          message: `Unplanned ${sessionsLabel(unplannedCount)}. Nothing was planned.`
        })
      );
      return;
    }
  }

  const { ok, ...response } = yield apiCall(shovelAiApi.planTasks, planRequest, actionId);
  if (!ok) {
    const message = planningErrorMessage(response.data);
    yield put(actions.planThisWeek.failure(message));
    if (onDone) yield call(onDone, false);
    alert(
      replan ? `Unplanned ${sessionsLabel(unplannedCount)}, but planning failed: ${message} Run Auto-plan again.` : message,
      NotificationMode.ERROR
    );
    return;
  }

  const createdEvents: CalendarEvent[] = response.data?.plannedTasks || [];
  const plannedMinutes = createdEvents.reduce(
    (sum, event) => sum + moment(event.end).diff(moment(event.start), 'minutes'),
    0
  );
  // The dialog closes first and the sessions come in after, so the student sees them fill the week rather than
  // finding them already there. They arrive tagged, which runs their intro, all at once.
  if (onDone) yield call(onDone, true);
  if (createdEvents.length > 0) {
    yield delay(AUTO_PLAN_DIALOG_CLOSE_MS);
    // A wave, day by day (autoPlanIntro.ts); extra time comes in with the session before it.
    const inOrder = [...createdEvents].sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
    const timeZone: string = yield select(semesterSelectors.getTimeZone);
    const delays = autoPlanDelays(
      autoPlanDays(
        inOrder.map(event => event.start),
        timeZone
      )
    );
    const delayOf = new Map(inOrder.map((event, index) => [event.id, delays[index]]));
    yield put(
      actions.setAutoPlanIntroSteps(
        inOrder.map((event, index) => ({ start: moment(event.start).valueOf(), delayMs: delays[index] }))
      )
    );
    yield put(
      actions.planTasks.success(
        createdEvents.map(
          event =>
            ({
              ...event,
              justAddedByAi: true,
              autoPlanIntro: true,
              autoPlanDelayMs: delayOf.get(event.id) || 0
            } as CalendarEvent)
        )
      )
    );
    yield fork(
      clearHighlightLater,
      createdEvents.map(event => Number(event.id)),
      AUTO_PLAN_INTRO_TOTAL_MS
    );
    yield fork(clearAutoPlanIntroLater);
  }
  // Done once the sessions are on the calendar, not before: in between, the calendar's button read the week as empty
  // and showed Auto-plan between Planning… and Replan (Petr, 2026-10-09).
  yield put(actions.planThisWeek.success(createdEvents));
  // The run's id in the activity log: the first step's, or the one the AI service gave the run.
  actionId = actionId ?? actionIdOf(response);
  // The missed sessions of the tasks just given time come off the calendar (Petr, 2026-10-19): their time is in this
  // plan now, and their Log time would credit it twice. They poof as the new sessions come in, and Undo brings them
  // back. A failure leaves them where they were, which is how it was before. Part of the run, so one Undo covers it.
  const missedRemoved: unknown[] = yield call(removeMissedSessions, request.semesterId, createdEvents, actionId);
  unplannedSessions = [...unplannedSessions, ...missedRemoved];
  // Details were in the dialog's preview; the toast only confirms what went on the calendar, and offers to take it back.
  if (createdEvents.length > 0 || unplannedSessions.length > 0 || reviewTaskIds.length > 0) {
    yield put(
      undoActions.record({
        entry: {
          kind: 'planned',
          plannedSessionIds: sessionIds(createdEvents),
          unplannedSessions,
          reviewTaskIds,
          actionId
        },
        message: formatPlanResult(plannedMinutes)
      })
    );
  } else {
    alert(formatPlanResult(plannedMinutes), NotificationMode.ALERT, 3000, true);
  }
}

// Returns the removed sessions as the server sent them, for Undo; none when nothing went or the call failed.
function* removeMissedSessions(semesterId: number, createdEvents: CalendarEvent[], actionId?: string) {
  const taskIds = plannedTaskIds(createdEvents as (CalendarEvent & { taskId?: number })[]);
  if (taskIds.length === 0) return [];
  const { ok, ...response } = yield apiCall(webPlannerApi.removeMissed, semesterId, { taskIds }, actionId);
  if (!ok) return [];
  yield call(applyUnplanResult, response.data, true);
  return response.data?.removedSessions || [];
}

// The time a session held, for the free time drawn in its place (markFreedTime).
const timeHeld = (event: CalendarEvent) => ({ start: moment(event.start).valueOf(), end: moment(event.end).valueOf() });

const sessionsLabel = (count: number) => `${count} session${count === 1 ? '' : 's'}`;

// Applies an unplan response to the store and returns how many blocks were removed. With `poof`, they go in a puff
// of smoke first: the Unplan dialog's, not Replan's, whose sessions come off behind "Planning…" and are replaced.
export function* applyUnplanResult(data: UnplanResponse | undefined, poof = false) {
  const deletedPlannedTaskIds: number[] = data?.deletedPlannedTaskIds || [];
  if (poof) {
    const poofed: number = yield call(poofPlannedSessions, deletedPlannedTaskIds);
    if (poofed) yield delay(POOF_SETTLE_MS);
  }
  const tasks: DeletePlannedTaskResponse[] = data?.tasks || [];
  const events: CalendarEvent[] = [];
  for (const plannedTaskId of deletedPlannedTaskIds) {
    const event = yield select(getPlannedEvent, plannedTaskId);
    if (event) events.push(event);
  }
  if (poof) yield call(markFreedTime, events.map(timeHeld));

  // One bulk action instead of a deletePlannedTask.success per block: each of those re-renders the calendar and
  // makes the pile listener refetch, which made unplanning a busy week take several seconds.
  yield put(actions.unplanWeek.success({ events, tasks }));
  return deletedPlannedTaskIds.length;
}

function* unplanWeek({ payload }: ReturnType<typeof actions.unplanWeek.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(webPlannerApi.unplan, semesterId, payload);
  if (!ok) {
    const message = getErrorMessage(response) || "Couldn't unplan. Try again.";
    yield put(actions.unplanWeek.failure(message));
    alert(message, NotificationMode.ERROR);
    return;
  }

  const count: number = yield call(applyUnplanResult, response.data, true);
  if (count === 0) {
    alert('Nothing to unplan in these dates.', NotificationMode.ALERT);
    return;
  }
  yield put(
    undoActions.record({
      entry: {
        kind: 'planned',
        plannedSessionIds: [],
        unplannedSessions: response.data?.removedSessions || [],
        reviewTaskIds: [],
        actionId: actionIdOf(response)
      },
      message: t.SESSIONS_UNPLANNED(count)
    })
  );
}

// One bulk action for the whole set, for the same reason applyUnplanResult exists: a success per block would
// re-render the calendar and make the pile listener refetch each time.
function* applyMarkUsedResult(data: MarkUsedResponse | undefined) {
  yield put(actions.markBlocksUsed.success(data || { plannedTasks: [], tasks: [], skippedPlannedTaskIds: [] }));
  return data?.plannedTasks.length || 0;
}

/**
 * Time logged on a session, or its tick taken off, kept for Undo without a toast, like a drag (Petr, 2026-10-24): the
 * block changes before the student's eyes. `timerWasOn`: its timer was running, whose minutes stay logged on an Undo.
 */
function* recordSessionUsed(
  before: PlannedSession | undefined,
  after: { id: number | string; isUsed: boolean; used?: number; pages?: number | null } | undefined,
  actionId: string | undefined,
  timerWasOn = false,
  usedBefore?: number
) {
  if (!before || !after || !!before.isUsed === after.isUsed) return;
  const title = before.title || '';
  const entry: Extract<UndoEntry, { kind: 'sessionUsed' }> = {
    kind: 'sessionUsed',
    marked: after.isUsed,
    plannedTaskId: Number(after.id),
    title,
    // A log that changed the time first (laps ticked or a time typed, saved just before it) is undone to the time from
    // before that, not to the changed one (Petr, 2026-10-29).
    before: {
      used: usedBefore ?? (timerWasOn ? after.used ?? 0 : before.used ?? 0),
      pages: before.pages ?? null
    },
    after: { used: after.used ?? 0, pages: after.pages ?? null },
    actionId
  };
  const message = after.isUsed ? t.SESSION_LOGGED(title) : t.SESSION_UNLOGGED(title);
  yield put(undoActions.record({ entry, message, silent: true }));
}

export function* markBlocksUsed({ payload }: ReturnType<typeof actions.markBlocksUsed.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { onDone, undoable, ...request } = payload;
  const before: PlannedSession | undefined =
    undoable && request.blocks.length === 1 ? yield select(getPlannedEvent, request.blocks[0].plannedTaskId) : undefined;
  const { ok, ...response } = yield apiCall(webPlannerApi.markUsed, semesterId, request);
  if (!ok) {
    const message = getErrorMessage(response) || "Couldn't log that session. Try again.";
    yield put(actions.markBlocksUsed.failure(message));
    return;
  }

  yield call(applyMarkUsedResult, response.data);
  yield call(recordSessionUsed, before, response.data?.plannedTasks[0], actionIdOf(response));
  // The dialog keeps its own task list, so it refetches from here rather than from a reducer.
  if (onDone) yield call(onDone, response.data);
}

function* clearAutoPlanIntroLater() {
  yield delay(AUTO_PLAN_INTRO_TOTAL_MS);
  yield put(actions.setAutoPlanIntroSteps(null));
}

function* reorderCourse(action: ReturnType<typeof actions.reorderCourses.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(courseApi.reorderCourses, semesterId, {
    items: action.payload.map(c => {
      return {
        id: c.id,
        ordinalNumber: c.ordinalNumber!
      };
    })
  });
  if (ok) {
    yield put(actions.reorderCourses.success(action.payload));
  } else {
    yield put(actions.reorderCourses.failure(getErrorMessage(response)));
  }
}

function* reorderActivities(action: ReturnType<typeof actions.reorderActivities.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(activityApi.reorderActivities, semesterId, {
    items: action.payload.map(a => {
      return {
        id: a.id,
        ordinalNumber: a.ordinalNumber!
      };
    })
  });
  if (ok) {
    yield put(actions.reorderActivities.success(action.payload));
  } else {
    yield put(actions.reorderActivities.failure(getErrorMessage(response)));
  }
}

function* resetNavigation() {
  yield put(workloadActions.selectCommitment());
}

function* updatePlannedTask(action: ReturnType<typeof actions.updatePlannedTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { id, mutate, request } = action.payload;
  // A time used on its way holds back marking the session used (pendingUsedSaves).
  const savesUsed = request.used !== undefined;
  if (savesUsed) startUsedSave(Number(id));
  let ok: boolean;
  let response: any;
  try {
    ({ ok, ...response } = yield apiCall(plannerApi.updatePlannedTask, semesterId, id, request));
  } finally {
    if (savesUsed) endUsedSave(Number(id));
  }
  if (ok) {
    yield put(actions.updatePlannedTask.success({ data: response.data, mutate }));
  } else {
    yield put(actions.updatePlannedTask.failure(getErrorMessage(response)));
  }
}
export function* markAsUsed(action: ReturnType<typeof actions.markAsUsed.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const activeTimer = yield select(timerSelectors.getActiveTimer);
  const { data, plannedTaskId, reading, courseId, usedBefore, askIn } = action.payload;
  // A time just typed into the session's timer goes first, or the server marks the planned time used instead.
  while (hasUsedSave(Number(plannedTaskId))) yield delay(25);
  // As it was, with that time, for Undo.
  const before: PlannedSession | undefined = yield select(getPlannedEvent, plannedTaskId);
  const { ok, ...response } = yield apiCall(plannerApi.markAsUsed, semesterId, plannedTaskId, data);
  if (ok) {
    // No pause before showing it any more: one used to let a Lottie check finish first, and with the calendar's redraw
    // after it, the session took well over a second to turn used (Petr: "a huge delay").
    yield put(actions.markAsUsed.success(response.data));
    const timerWasOn = !!activeTimer && Number(activeTimer.plannedTaskId) === Number(plannedTaskId);
    // Under a minute on the timer, the server logged the plan instead (Petr, 2026-10-30): Undo takes that off too.
    const timedAMinute =
      timerWasOn &&
      getElapsedSeconds(
        activeTimer.timePassedInSeconds,
        activeTimer.startedInUtc,
        activeTimer.status === TimerStatus.ACTIVE
      ) >= 60;
    yield call(recordSessionUsed, before, response.data.plannedTaskDto, actionIdOf(response), timedAMinute, usedBefore);
    const session = response.data.plannedTaskDto;
    // stop if there is active timer for that planned task
    if (activeTimer && activeTimer.plannedTaskId === plannedTaskId && session.isUsed) {
      // The top bar's pill goes once the time thrown at the streak's flame has landed, and closes smoothly: gone at once,
      // the bar shifted under the dot and it landed mid-bar (Petr, 2026-10-17). The server has ended the timer already.
      const landing = msUntilStreakLanding();
      if (landing > 0) yield delay(landing + STREAK_SETTLE_MS);
      const closing = closeTimerPill();
      if (closing > 0) yield delay(closing);
      yield put(timerActions.clearSession());
    }
    // Kept going past the plan, or logged longer than it was (Petr, 2026-10-29: 1h 30m typed on an hour's session
    // left it over the next one): the study sessions it now runs into are bumped down behind it, once (timer spec step 5).
    const grew = !!before && new Date(session.end).getTime() > new Date(before.end).getTime();
    if (session.isUsed && (timerWasOn || grew)) yield call(placeSessionsAfterOvertime, session);

    // The book's "Time per page" question, when the session's pace is 20% or more off the book's and it is now used:
    // in the session's popup if it waits for it, else as an overlay once the ✓'s flight is over (askEstimateQuestion).
    const { isUsed, pages, used } = response.data.plannedTaskDto;
    const newTimePerPage = isUsed && reading && pages ? Math.floor((used * 60) / pages) : undefined;
    if (reading && newTimePerPage !== undefined && paceDiffersEnough(newTimePerPage, reading.secondsPerPage)) {
      yield spawn(
        askEstimateQuestion,
        taskActions.openNewEstimateDialog({ reading, newTimePerPage, taskId: response.data.taskId, courseId: courseId! }),
        askIn
      );
    } else {
      answerWaitingPopup(askIn, false);
    }
  } else {
    answerWaitingPopup(askIn, false);
    yield put(actions.markAsUsed.failure(getErrorMessage(response)));
  }
}

/**
 * A session timed past its plan and marked used may have run into the study sessions after it: they're bumped down
 * behind it as ▶ bumps them (utils/bump.ts planAfterOvertime; with Bumpers on, on into the next days), sliding there,
 * saved all at once, with Undo. Classes and events never move.
 */
function* placeSessionsAfterOvertime(block: { id?: number | string; start: any; end: any }) {
  const state: RootState = yield select(s => s);
  const theme = storage.getTheme() === 'light' ? lightTheme : darkTheme;
  const { chain, items, context } = getBumpInputs(state, { theme });
  // Without the awake times there's no study time to bump into: they stay where they are.
  if (!context.awake.length) return;
  const { moves, creates, removes } = planAfterOvertime({
    blockStart: new Date(block.start).getTime(),
    blockEnd: new Date(block.end).getTime(),
    now: Date.now(),
    items: items.filter(item => item.id !== String(block.id)),
    breaks: withPlanningDefaults((state.settings.userSettings as any)?.planningPreferences),
    context,
    chain
  });
  if (!moves.length && !removes.length) return;
  const semesterId = yield select(semesterSelectors.getId);
  const plannedEvents = state.calendar.plannedEvents as any;
  const iso = (at: number | string) => new Date(at).toISOString();
  const places = moves
    .filter(move => plannedEvents[move.id])
    .map(move => ({ id: Number(move.id), start: iso(plannedEvents[move.id].start), end: iso(plannedEvents[move.id].end) }));
  const toSave = moves.map(move => ({ id: Number(move.id), start: iso(move.start), end: iso(move.end) }));
  followBlocks(moves.map(move => move.id));
  yield put(actions.previewPlannedMoves(toSave));
  // Sessions split or taken off go in one request, all or none.
  const rearranging = creates.length > 0 || removes.length > 0;
  const { ok, ...response } = rearranging
    ? yield apiCall(webPlannerApi.rearrangeSessions, semesterId, {
        moves: toSave,
        pieces: creates.map(piece => ({ fromId: Number(piece.fromId), start: iso(piece.start), end: iso(piece.end) })),
        removeIds: removes.map(gone => Number(gone.id))
      })
    : yield apiCall(webPlannerApi.movePlannedTasks, semesterId, toSave);
  if (!ok) {
    followBlocks(places.map(place => place.id));
    yield put(actions.previewPlannedMoves(places));
    return;
  }
  const saved = yield select((s: RootState) => s.calendar.plannedEvents);
  const moved: CalendarEvent[] = (rearranging ? response.data?.moved : response.data) || [];
  const created: CalendarEvent[] = (rearranging && response.data?.created) || [];
  // One redraw for the lot.
  yield put(
    applyTogether([
      ...moved.map(newEvent => actions.resizePlannedTask.success({ oldEvent: saved[newEvent.id!], newEvent })),
      ...(created.length ? [actions.planTasks.success(created)] : [])
    ])
  );
  if (rearranging && response.data?.deletedPlannedTaskIds?.length) yield call(applyUnplanResult, response.data, true);

  const time24 = !!(state.settings.userSettings as any)?.time24Format;
  const clock = time24 ? 'HH:mm' : 'h:mma';
  const [first] = moves;
  // "Moved “Essay” to 17:45 (+2 more)", "… to Fri 08:00" on another day; " · 1 off the plan" for any taken off.
  const when = first && toMomentDate(new Date(first.start));
  const today = toMomentDate(new Date()).format('YYYY-MM-DD');
  const parts = [
    ...(first && when
      ? [
          t.SESSIONS_MOVED(
            first.title ? `“${first.title}”` : t.TIMER_NEXT_SESSION,
            when.format(when.format('YYYY-MM-DD') === today ? clock : `ddd ${clock}`),
            moves.length - 1
          )
        ]
      : []),
    ...(removes.length
      ? [first ? t.TIMER_BUMP_OFF(removes.length) : capitalised(t.TIMER_BUMP_TAKES_OFF(removes.length, true))]
      : [])
  ];
  yield put(
    undoActions.record({
      entry: {
        kind: 'sessionsBumped',
        places,
        to: moved.map(event => ({ id: Number(event.id), start: iso(event.start as any), end: iso(event.end as any) })),
        createdIds: created.map(event => Number(event.id)),
        removedSessions: (rearranging && response.data?.removedSessions) || [],
        actionId: actionIdOf(response)
      },
      message: parts.join(' · ')
    })
  );
}

const capitalised = (words: string) => words.charAt(0).toUpperCase() + words.slice(1);

function* duplicatePlannedTask({ payload }: ReturnType<typeof actions.duplicatePlannedTask.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(weekApi.duplicatePlannedTask, semesterId, payload);
  if (ok) {
    yield put(actions.duplicatePlannedTask.success(response.data));
  } else {
    yield put(actions.duplicatePlannedTask.failure(getErrorMessage(response)));
  }
}

function* changeEventDefaultColor({ payload }: ReturnType<typeof actions.changeEventDefaultColor.request>) {
  const semesterId = yield select(semesterSelectors.getId);
  const { ok, ...response } = yield apiCall(weekApi.changeEventDefaultColor, semesterId, payload);
  if (ok) {
    yield put(actions.changeEventDefaultColor.success(payload));
  } else {
    yield put(actions.changeEventDefaultColor.failure(getErrorMessage(response)));
  }
}

export default function*() {
  yield all([
    takeLatest(actions.calendarData.request, getCalendarData),
    takeLatest(actions.getDatesContainingTasks.request, getDatesContainingTasks),
    takeLatest(actions.getPlannedTasks.request, getPlannedTasks),
    takeLatest(actions.commitmentsData.request, getCommitmentsData),
    // Every one runs to the end: a quick second drag cancelled the first's success, so its save never reached the
    // calendar or Undo.
    takeEvery(actions.movePlannedTask.request, movePlannedTask),
    takeEvery(actions.bumpSessions, bumpSessions),
    takeEvery(actions.resizePlannedTask.request, resizePlannedTask),
    // Every unplan finishes: the server has already removed the session by the time it poofs, and a second unplan
    // cancelling the first (takeLatest) left that one on the calendar.
    takeEvery(actions.deletePlannedTask.request, deletePlannedTask),
    takeLatest(actions.getTasksOnDate.request, getTasksOnDate),
    takeLatest(actions.createCourse.request, createCourse),
    takeLatest(actions.updateCourse.request, updateCourse),
    takeLatest(actions.deleteCourse.request, deleteCourse),
    takeLatest(actions.createActivity.request, createActivity),
    takeLatest(actions.updateActivity.request, updateActivity),
    takeLatest(actions.deleteActivity.request, deleteActivity),
    takeLatest(actions.planTask.request, planTask),
    takeLatest(actions.updateCalendarSlatHeight, updateCalendarSlatHeight),
    takeLatest(actions.getTasksForPlanning.request, getTasksForPlanning),
    takeLatest(actions.planTasks.request, planTasks),
    takeLeading(actions.planThisWeek.request, planThisWeek),
    takeLeading(actions.unplanWeek.request, unplanWeek),
    // Every request, not the first of a burst: ticking the ✓ discs of several blocks in a row is several requests, and
    // the endpoint is idempotent.
    takeEvery(actions.markBlocksUsed.request, markBlocksUsed),
    takeLatest(actions.resetNavigation, resetNavigation),
    takeLatest(actions.reorderCourses.request, reorderCourse),
    takeLatest(actions.reorderActivities.request, reorderActivities),
    takeLatest(actions.updatePlannedTask.request, updatePlannedTask),
    takeLatest(actions.markAsUsed.request, markAsUsed),
    takeLatest(actions.duplicatePlannedTask.request, duplicatePlannedTask),
    takeLatest(actions.changeEventDefaultColor.request, changeEventDefaultColor)
  ]);
}
